import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Route, Switch, useLocation } from "wouter";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "@/auth/AuthProvider";
import { Login } from "@/auth/Login";
import { Onboarding } from "@/auth/Onboarding";
import { ToastProvider } from "@/components/ui/Toast";
import { AlmacenProvider } from "@/lib/almacen";
import { Shell } from "@/components/layout/Shell";
import { Cargando, ErrorBloque, Boton } from "@/components/ui";
import { Hoy } from "@/features/hoy/Hoy";
import { Contar } from "@/features/contar/Contar";
import { Entradas, Bajas, Transferir } from "@/features/movimientos/Movimientos";
import { VentasDia } from "@/features/movimientos/VentasDia";
import { Inventario } from "@/features/inventario/Inventario";
import { Historial } from "@/features/historial/Historial";
import { Importar } from "@/features/importar/Importar";
import { Ajustes } from "@/features/ajustes/Ajustes";

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Refrescar al volver a la pestaña: en Contar zona se apaga por pantalla (filas sin guardar).
      refetchOnWindowFocus: true,
      retry: (n, err) => n < 2 && !String((err as { code?: string })?.code ?? "").startsWith("42"),
    },
  },
});

/** Redirige a la pantalla inicial según el rol, leyendo el rol en el momento (no al montar). */
function Inicio() {
  const { esAdmin } = useAuth();
  const [, navegar] = useLocation();
  useEffect(() => { navegar(esAdmin ? "/hoy" : "/contar", { replace: true }); }, [esAdmin, navegar]);
  return <Cargando texto="Entrando…" />;
}

function Rutas() {
  const { session, perfil, perfilError, inactivo, recargarPerfil, salir } = useAuth();
  if (session === null && perfil === null) return <Login />;
  if (perfilError) return <div className="mx-auto max-w-md p-6"><ErrorBloque mensaje={perfilError} reintentar={() => recargarPerfil()} /><Boton variante="fantasma" className="mt-3" onClick={salir}>Salir</Boton></div>;
  if (inactivo) return <div className="mx-auto max-w-md p-6"><ErrorBloque mensaje="Tu acceso fue desactivado. Habla con el administrador de tu empresa." /><Boton variante="fantasma" className="mt-3" onClick={salir}>Salir</Boton></div>;
  if (perfil === undefined) return <Cargando texto="Entrando…" />;
  if (perfil === null) return <Onboarding />;
  return (
    <AlmacenProvider>
      <Shell>
        <Switch>
          <Route path="/" component={Inicio} />
          <Route path="/hoy" component={Hoy} />
          <Route path="/contar/:zonaId?" component={Contar} />
          <Route path="/entradas" component={Entradas} />
          <Route path="/bajas" component={Bajas} />
          <Route path="/ventas" component={VentasDia} />
          <Route path="/transferir" component={Transferir} />
          <Route path="/inventario" component={Inventario} />
          <Route path="/historial" component={Historial} />
          <Route path="/importar" component={Importar} />
          <Route path="/ajustes" component={Ajustes} />
          <Route component={Inicio} />
        </Switch>
      </Shell>
    </AlmacenProvider>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <ToastProvider>
        <AuthProvider>
          <Rutas />
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
